import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { Button } from '@/components/ui/button';
import {
  PREVIOUS_READING_TAKE,
  PREVIOUS_READING_TAKE_HELP,
  PREVIOUS_READING_TITLE,
} from '@/lib/analyst-reading';
import type { ProtocolStepDraft } from '@matter_extractor/shared';
import { hasAnalystReading, resolveAnalystReading } from '@matter_extractor/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * Le bloc gris « Lecture d'avant #196 (plus servie) » (analyste.md §3.8, K14 ;
 * planche `AnalysteAEtats`, « Protocole d'avant #196 ») : la lecture que les
 * protocoles écrits avant la section portent encore dans leur contenu, montrée
 * REPLIÉE et en lecture, avec « Reprendre dans la passe 1 ».
 *
 * ## It stays in the content, and it is no longer served
 *
 * `compat:prod` reads every version in production back with the new code and would
 * refuse the tag of a code that dropped it, so the reading of before stays where it
 * was, untouched. Nothing edits it and nothing serves it: the two readings of the
 * section are what the analyst reads now. The block says so in its title and shows the
 * text for what it is - a quotation, in the grey of the parts written in code - so an
 * operator moving to the section does not lose the words the protocol was written
 * with.
 *
 * ## « Reprendre dans la passe 1 » is ONE write
 *
 * It copies the text, as stored - its `{{objectif:id}}` kept, so the citations follow
 * the objectives they name - into the reading of the pass 1, REPLACING what is there.
 * One `onTake`, which the section answers with one write of the draft: the undo stack
 * of the editor then brings the previous text back in one step. The block is shown
 * only while the reading of before is not empty (spaces alone count as empty, N35),
 * and « Voir » - a version, a retired protocol - has no button: nothing is writable.
 *
 * The body is drawn RESOLVED (`resolveAnalystReading`): the names the draft gives the
 * objectives, « (objectif retiré) » for a ghost, in plain text - what the analyst
 * would have read of it.
 */

export interface AnalystPreviousReadingProps {
  /** The reading of before #196, as stored in the content. */
  readonly reading: string;
  /** The steps its citations resolve on. */
  readonly steps: readonly ProtocolStepDraft[];
  /** « Voir », or a retired protocol: nothing is writable. */
  readonly readOnly: boolean;
  readonly onTake: () => void;
}

export function AnalystPreviousReading({
  reading,
  steps,
  readOnly,
  onTake,
}: AnalystPreviousReadingProps) {
  const titleId = useId();
  const bodyId = useId();
  const helpId = useId();
  const [open, setOpen] = useState(false);
  if (!hasAnalystReading(reading)) return null;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <section
      aria-labelledby={titleId}
      data-testid={PROTOCOL_TEST_IDS.analystPrevious}
      className="flex min-w-0 flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3.5 pt-2.5 pb-3.5"
    >
      <h3 id={titleId} className="font-semibold text-gray-700 text-sm">
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.analystPreviousToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((was) => !was)}
          className="inline-flex min-h-7 cursor-pointer items-center gap-1.5 text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Chevron aria-hidden className="size-3.5 shrink-0" />
          <span>{PREVIOUS_READING_TITLE}</span>
        </button>
      </h3>
      <div id={bodyId} hidden={!open} className="flex min-w-0 flex-col gap-3">
        <p className="whitespace-pre-wrap break-words text-caption text-gray-600">
          {resolveAnalystReading(reading.trim(), steps)}
        </p>
        {readOnly ? null : (
          <div className="flex flex-col items-start gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={PROTOCOL_TEST_IDS.analystPreviousTake}
              aria-describedby={helpId}
              onClick={onTake}
            >
              {PREVIOUS_READING_TAKE}
            </Button>
            <p id={helpId} className="text-gray-600 text-xs leading-4">
              {PREVIOUS_READING_TAKE_HELP}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
